import { useCallback } from "react"
import { useFetcher } from "react-router"
import { SharedRecordListTab, type RecordListState } from "../shared/SharedRecordList"
import type { EnhancedConfigData, RecordListConfig } from "./brand-lenses-configuration"

interface FetcherData {
  success?: boolean
  action?: string
  errorMessage?: string
}

interface RecordListTabProps {
  data: EnhancedConfigData
  config: RecordListConfig
}

export function RecordListTab({ data, config }: RecordListTabProps) {
  const fetcher = useFetcher<FetcherData>()
  const pendingAction = (fetcher.json as { action?: "SAVE_SECTION" | "REMOVE_SECTION" } | undefined)?.action ?? null

  const handleSave = useCallback(
    (state: RecordListState) => {
      fetcher.submit(
        {
          action: "SAVE_SECTION",
          organisation: data.loadedOrganisation,
          project: data.loadedProject,
          jsonPath: "$.recordList",
          value: {
            foldersViewEnabled: state.foldersViewEnabled,
            showSearch: state.showSearch,
            summariser: { enabled: state.summariserEnabled },
            ioa: { disabled: false },
            userTags: state.userTags.map((tag) => ({
              ...tag,
              enumeration: (tag.enumeration ?? []).map((item) => ({ ...item })),
            })),
            evidenceCounts: state.evidenceCounts.map((ec) => ({ ...ec })),
            autoTags: state.autoTags.map((autoTag) => ({
              ...autoTag,
              enumeration: (autoTag.enumeration ?? []).map((item) => ({ ...item })),
            })),
          },
          concurrency: data.existingConfig?.concurrency ?? "",
        },
        { method: "post", encType: "application/json" },
      )
    },
    [fetcher, data],
  )

  const handleRemove = useCallback(() => {
    fetcher.submit(
      {
        action: "REMOVE_SECTION",
        organisation: data.loadedOrganisation,
        project: data.loadedProject,
        jsonPath: "$.recordList",
        concurrency: data.existingConfig?.concurrency ?? "",
      },
      { method: "post", encType: "application/json" },
    )
  }, [fetcher, data])

  return (
    <SharedRecordListTab
      initialConfig={{
        userTags: config.userTags ?? [],
        evidenceCounts: (config.evidenceCounts ?? []).map((ec) => ({
          ...ec,
          criteriaLabel: ec.criteriaLabel ?? ec.name,
        })),
        autoTags: config.autoTags ?? [],
        foldersViewEnabled: config.foldersViewEnabled ?? false,
        showSearch: config.showSearch ?? false,
        summariserEnabled: config.summariser?.enabled ?? false,
      }}
      onSave={handleSave}
      onRemove={handleRemove}
      isFetching={fetcher.state !== "idle"}
      currentAction={pendingAction}
      actionResult={fetcher.data}
      tagConfigType="brand-lenses"
      data={{
        loadedOrganisation: data.loadedOrganisation,
        loadedProject: data.loadedProject,
        evidenceCountData: data.evidenceCountData,
        userTagTypes: data.userTagTypes,
        autoTagTypes: data.autoTagTypes,
      }}
    />
  )
}
